/** @file Changes the bone/slot selection: replace (clears vertices and keys), add or toggle (W3-36). */
import type { Id } from '@pose2d/format/types';
import { selectionStore } from './selectionStore';

/** How a pick combines with the selection: plain click, Shift-click, Ctrl/Cmd-click. */
export type SelectMode = 'replace' | 'add' | 'toggle';

/** The ids one pick or marquee hit. */
export interface SelectionChange {
  bones: Id[];
  slots: Id[];
}

/** One list combined by mode; order is selection order (the last bone is the active one). */
function merge(current: readonly Id[], hit: readonly Id[], mode: SelectMode): Id[] {
  if (mode === 'replace') return [...hit];
  const fresh = hit.filter((id) => !current.includes(id));
  return mode === 'add' ? [...current, ...fresh] : [...current.filter((id) => !hit.includes(id)), ...fresh];
}

/**
 * Applies a pick to `selection.bones` and `selection.slots`. `'replace'` also clears `selection.vertices` and
 * `selection.keys` (a bone or slot pick wins the Inspector); `'add'` and `'toggle'` leave them.
 */
export function updateSelection(change: SelectionChange, mode: SelectMode): void {
  const s = selectionStore.getState();
  const bones = merge(s.bones, change.bones, mode);
  const slots = merge(s.slots, change.slots, mode);
  selectionStore.setState(mode === 'replace' ? { bones, slots, vertices: [], keys: [] } : { bones, slots });
}
